// Spotify OAuth — Authorization Code with PKCE flow.
// PKCE means we don't ship a client_secret in the browser; the proof of
// possession is the code_verifier we generated locally.
// Docs: https://developer.spotify.com/documentation/web-api/tutorials/code-pkce-flow

const CLIENT_ID = import.meta.env.VITE_SPOTIFY_CLIENT_ID
const REDIRECT_URI = import.meta.env.VITE_SPOTIFY_REDIRECT_URI

// Comma-separated list of Spotify user IDs who are allowed to be instructors.
// If empty, anyone who can complete Spotify OAuth becomes an instructor —
// in that case the only gate is Spotify's "Development Mode" user limit
// (managed in the Spotify Developer Dashboard → User Management).
const INSTRUCTOR_ALLOWLIST = (import.meta.env.VITE_INSTRUCTOR_SPOTIFY_IDS || '')
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean)

/**
 * Returns true if the given Spotify user ID is authorized as an instructor.
 * Empty allowlist = no in-app gate (defaults to "anyone who can OAuth").
 */
export function isAuthorizedInstructor(spotifyUserId) {
  if (INSTRUCTOR_ALLOWLIST.length === 0) return true
  return INSTRUCTOR_ALLOWLIST.includes(spotifyUserId)
}

const AUTH_URL = 'https://accounts.spotify.com/authorize'
const TOKEN_URL = 'https://accounts.spotify.com/api/token'

const SCOPES = [
  'user-read-currently-playing',
  'user-read-recently-played',
  'user-read-playback-state',
].join(' ')

const STORAGE_KEYS = {
  accessToken: 'peak.spotify.access_token',
  refreshToken: 'peak.spotify.refresh_token',
  expiresAt: 'peak.spotify.expires_at',
  codeVerifier: 'peak.spotify.code_verifier',
  spotifyUserId: 'peak.spotify.user_id',
  spotifyDisplayName: 'peak.spotify.display_name',
}

function base64UrlEncode(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
}

async function sha256(text) {
  const data = new TextEncoder().encode(text)
  return crypto.subtle.digest('SHA-256', data)
}

function generateRandomString(length) {
  const arr = new Uint8Array(length)
  crypto.getRandomValues(arr)
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('').slice(0, length)
}

export async function startLogin() {
  if (!CLIENT_ID || !REDIRECT_URI) {
    throw new Error('Spotify env vars are missing. Check .env.local.')
  }
  const codeVerifier = generateRandomString(64)
  const codeChallenge = base64UrlEncode(await sha256(codeVerifier))
  localStorage.setItem(STORAGE_KEYS.codeVerifier, codeVerifier)

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: 'code',
    redirect_uri: REDIRECT_URI,
    scope: SCOPES,
    code_challenge_method: 'S256',
    code_challenge: codeChallenge,
  })
  window.location.href = `${AUTH_URL}?${params.toString()}`
}

export async function handleCallback(code) {
  const codeVerifier = localStorage.getItem(STORAGE_KEYS.codeVerifier)
  if (!codeVerifier) throw new Error('Missing PKCE code_verifier in storage')

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: REDIRECT_URI,
    client_id: CLIENT_ID,
    code_verifier: codeVerifier,
  })
  const res = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Token exchange failed: ${res.status} ${text}`)
  }
  const json = await res.json()
  storeTokens(json)
  const me = await spotifyGet('/me')

  // Layer 2 gate: the in-app allowlist. Spotify's User Management already
  // gates who can complete OAuth at all; this gives us a finer-grained knob
  // (e.g., for accounts that can sign in but shouldn't run the night).
  if (!isAuthorizedInstructor(me.id)) {
    logout()
    throw new Error(
      `Spotify account "${me.id}" is not on the instructor allowlist. ` +
      `Ask the admin to add this Spotify user ID to VITE_INSTRUCTOR_SPOTIFY_IDS.`,
    )
  }

  localStorage.setItem(STORAGE_KEYS.spotifyUserId, me.id)
  // display_name is the friendly version Spotify shows in their app — much
  // nicer to surface to other instructors than the raw user ID. Fall back to
  // ID if Spotify didn't return one.
  localStorage.setItem(STORAGE_KEYS.spotifyDisplayName, me.display_name || me.id)
  return me
}

function storeTokens({ access_token, refresh_token, expires_in }) {
  const expiresAt = Date.now() + (expires_in - 60) * 1000
  localStorage.setItem(STORAGE_KEYS.accessToken, access_token)
  if (refresh_token) localStorage.setItem(STORAGE_KEYS.refreshToken, refresh_token)
  localStorage.setItem(STORAGE_KEYS.expiresAt, String(expiresAt))
}

async function refresh() {
  const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken)
  if (!refreshToken) throw new Error('Not logged in (no refresh token)')

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: CLIENT_ID,
  })
  const res = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  if (!res.ok) {
    logout()
    throw new Error(`Token refresh failed: ${res.status}`)
  }
  const json = await res.json()
  storeTokens(json)
  return json.access_token
}

export async function getAccessToken() {
  const token = localStorage.getItem(STORAGE_KEYS.accessToken)
  const expiresAt = Number(localStorage.getItem(STORAGE_KEYS.expiresAt) || 0)
  if (token && Date.now() < expiresAt) return token
  return refresh()
}

export function isLoggedIn() {
  return Boolean(localStorage.getItem(STORAGE_KEYS.refreshToken))
}

export function getStoredSpotifyUserId() {
  return localStorage.getItem(STORAGE_KEYS.spotifyUserId)
}

export function getStoredDisplayName() {
  // Prefer display name; fall back to user ID if for some reason it wasn't stored.
  return localStorage.getItem(STORAGE_KEYS.spotifyDisplayName) ||
         localStorage.getItem(STORAGE_KEYS.spotifyUserId)
}

export function logout() {
  Object.values(STORAGE_KEYS).forEach((k) => localStorage.removeItem(k))
}

export async function spotifyGet(path, { signal } = {}) {
  let token = await getAccessToken()
  let res = await fetch(`https://api.spotify.com/v1${path}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  })
  if (res.status === 401) {
    token = await refresh()
    res = await fetch(`https://api.spotify.com/v1${path}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    })
  }
  if (res.status === 204) return null
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Spotify ${path} failed: ${res.status} ${text}`)
  }
  return res.json()
}
